import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Avatar,
  Button,
  TextField,
  Link,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper
} from '@mui/material';
import { VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { authService } from '../../services/authService';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../store/slices/uiSlice';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }
    
    setLoading(true);
    setError('');
    setSuccess(false);
    
    try {
      // パスワードリセット用のコードを送信
      const result = await authService.forgotPassword(email);
      
      if (result.success) {
        setSuccess(true);
        dispatch(showNotification({
          message: 'パスワードリセット用のコードが送信されました',
          type: 'success'
        }));
        
        // パスワードリセット確認ページに遷移
        navigate('/reset-password', { 
          state: { 
            username: email
          } 
        });
      } else {
        setError(result.message || 'パスワードリセットの処理中にエラーが発生しました');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'パスワードリセットの処理中にエラーが発生しました';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 600,
        mx: 'auto',
        p: 2
      }}
    >
      <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
        <VpnKeyIcon />
      </Avatar>
      <Typography component="h1" variant="h5">
        パスワードをお忘れですか？
      </Typography>
      <Typography variant="body2" sx={{ mt: 1, mb: 3, textAlign: 'center' }}>
        登録されているメールアドレスにパスワードリセット用のコードを送信します。
      </Typography>
      
      {error && <Alert severity="error" sx={{ mt: 2, width: '100%' }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2, width: '100%' }}>パスワードリセット用のコードが送信されました</Alert>}
      
      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="メールアドレス"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'コードを送信'}
        </Button>
        <Grid container>
          <Grid item>
            <Link component={RouterLink} to="/login" variant="body2">
              ログインページに戻る
            </Link>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
